import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { refreshLeadOptions } from "../lib/useLeadOptions";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [error, setError] = useState("");
  // Multi-business: which brands this session may operate in. One entry for
  // staff; every active brand for a super admin.
  const [businesses, setBusinesses] = useState([]);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  // Load the switcher list once we know who the user is.
  useEffect(() => {
    if (!user || user === false) {
      setBusinesses([]);
      return;
    }
    api
      .get("/businesses")
      .then(({ data }) => setBusinesses(data.businesses || []))
      .catch(() => setBusinesses([]));
  }, [user]);

  // CRITICAL race-condition guard from the Emergent Auth playbook:
  // If we've just returned from Google OAuth, the URL fragment carries a
  // session_id we need to trade for our own session BEFORE running /auth/me
  // (otherwise /auth/me will 401 first and bounce the user to /login).
  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      // Extract the session_id from the fragment
      const match = hash.match(/session_id=([^&]+)/);
      const sessionId = match ? decodeURIComponent(match[1]) : "";
      // Strip the fragment immediately so refreshes don't re-run the exchange
      // and so the URL bar stays clean.
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      if (!sessionId) {
        fetchMe();
        return;
      }
      (async () => {
        try {
          const { data } = await api.post("/auth/google/complete", { session_id: sessionId });
          setUser(data);
        } catch (e) {
          setError(formatApiError(e));
          setUser(false);
        }
      })();
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      setError("");
      const { data } = await api.post("/auth/login", { email, password });
      // OTP flow: server asks for a code instead of returning a session
      if (data?.otp_required) {
        return {
          otpRequired: true,
          otpRef: data.otp_ref,
          emailMasked: data.email_masked,
          expiresIn: data.expires_in,
          delivered: data.delivered,
          deliveryReason: data.delivery_reason,
        };
      }
      setUser(data);
      return { ok: true };
    } catch (e) {
      setError(formatApiError(e));
      return { ok: false };
    }
  };

  const verifyOtp = async (otpRef, code) => {
    try {
      setError("");
      const { data } = await api.post("/auth/verify-otp", { otp_ref: otpRef, code });
      setUser(data);
      return { ok: true };
    } catch (e) {
      setError(formatApiError(e));
      return { ok: false };
    }
  };

  const resendOtp = async (otpRef) => {
    try {
      const { data } = await api.post("/auth/resend-otp", { otp_ref: otpRef });
      return { ok: true, ...data };
    } catch (e) {
      setError(formatApiError(e));
      return { ok: false };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  // Super admins only. Mints a fresh session pinned to the new brand, so all
  // subsequent requests read that brand's database. Cached lead options belong
  // to the old brand and must be dropped.
  const switchBusiness = async (slug) => {
    try {
      setError("");
      const { data } = await api.post("/businesses/switch", { business: slug });
      setUser(data);
      await refreshLeadOptions();
      return { ok: true };
    } catch (e) {
      setError(formatApiError(e));
      return { ok: false };
    }
  };

  // can(actionKey) — true if user is admin OR has the permission granted.
  // Use throughout the UI to hide/disable buttons the user can't action.
  const can = useCallback(
    (action) => {
      if (!user || user === false) return false;
      if (user.role === "admin") return true;
      return Boolean(user.permissions?.[action]);
    },
    [user],
  );

  return (
    <AuthContext.Provider value={{ user, error, login, verifyOtp, resendOtp, logout, refresh: fetchMe, refreshMe: fetchMe, can, businesses, switchBusiness }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
